import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { API } from '../App';
import { 
  Home, Calendar, Users, Store, Info, Mail, FileText, Shield, 
  ChevronRight, Map, Building2, CalendarDays
} from 'lucide-react';
import AutoTranslatedText from '../components/AutoTranslatedText';
import { parseLocalDate } from '../utils/dateUtils';

const SitemapPage = () => {
  const { t } = useTranslation();
  const [events, setEvents] = useState([]);
  const [booths, setBooths] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [eventsRes, boothsRes] = await Promise.all([
          axios.get(`${API}/events`),
          axios.get(`${API}/booths`)
        ]);
        setEvents(eventsRes.data || []);
        setBooths(boothsRes.data || []);
      } catch (error) {
        console.error('Error fetching sitemap data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const mainPages = [
    { path: '/', icon: Home, label: t('nav.home'), description: t('sitemap.home_desc') },
    { path: '/events', icon: Calendar, label: t('nav.events'), description: t('sitemap.events_desc') },
    { path: '/exhibitors', icon: Store, label: t('nav.exhibitors'), description: t('sitemap.exhibitors_desc') },
    { path: '/calendar', icon: CalendarDays, label: t('sitemap.calendar'), description: t('sitemap.calendar_desc') },
    { path: '/about', icon: Info, label: t('nav.about'), description: t('sitemap.about_desc') },
    { path: '/contact', icon: Mail, label: t('nav.contact'), description: t('sitemap.contact_desc') },
  ];

  const legalPages = [
    { path: '/privacy', icon: Shield, label: t('footer.privacy'), description: t('sitemap.privacy_desc') },
    { path: '/terms', icon: FileText, label: t('footer.terms'), description: t('sitemap.terms_desc') },
  ];

  const SectionTitle = ({ icon: Icon, title }) => (
    <div className="flex items-center gap-3 mb-4 pb-2 border-b border-slate-200">
      <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
        <Icon className="h-5 w-5 text-primary" />
      </div>
      <h2 className="text-xl font-bold text-slate-800">{title}</h2>
    </div>
  );

  const PageLink = ({ path, icon: Icon, label, description }) => (
    <Link 
      to={path} 
      className="group flex items-start gap-3 p-3 rounded-lg hover:bg-slate-50 transition-colors"
    >
      <Icon className="h-5 w-5 text-slate-400 group-hover:text-primary mt-0.5 transition-colors" />
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <span className="font-medium text-slate-700 group-hover:text-primary transition-colors">
            {label}
          </span>
          <ChevronRight className="h-4 w-4 text-slate-300 group-hover:text-primary group-hover:translate-x-1 transition-all" />
        </div>
        <p className="text-sm text-slate-500 mt-0.5">{description}</p>
      </div>
    </Link>
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white py-12">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center">
              <Map className="h-7 w-7 text-primary" />
            </div>
          </div>
          <h1 className="text-3xl font-bold text-slate-900 mb-2">{t('sitemap.title')}</h1>
          <p className="text-slate-600 max-w-2xl mx-auto">{t('sitemap.subtitle')}</p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Main Pages */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <SectionTitle icon={Home} title={t('sitemap.main_pages')} />
            <div className="space-y-1">
              {mainPages.map((page) => (
                <PageLink key={page.path} {...page} />
              ))}
            </div>
          </div>

          {/* Legal Pages */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <SectionTitle icon={FileText} title={t('sitemap.legal_pages')} />
            <div className="space-y-1">
              {legalPages.map((page) => (
                <PageLink key={page.path} {...page} />
              ))}
            </div>
            
            {/* XML Sitemap Link for SEO */}
            <div className="mt-6 pt-4 border-t border-slate-200">
              <p className="text-xs text-slate-500 mb-2">{t('sitemap.for_search_engines')}</p>
              <a 
                href="/api/sitemap" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-sm text-primary hover:underline"
              >
                XML Sitemap →
              </a>
            </div>
          </div>

          {/* Events */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <SectionTitle icon={Calendar} title={t('sitemap.events_section')} />
            {loading ? (
              <div className="animate-pulse space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-12 bg-slate-100 rounded-lg" />
                ))}
              </div>
            ) : events.length === 0 ? (
              <p className="text-slate-500 text-sm py-4">{t('sitemap.no_events')}</p>
            ) : (
              <div className="space-y-1 max-h-80 overflow-y-auto">
                {events.map((event) => (
                  <Link 
                    key={event.id}
                    to={`/events/${event.id}`}
                    className="group flex items-start gap-3 p-3 rounded-lg hover:bg-slate-50 transition-colors"
                  >
                    <Calendar className="h-5 w-5 text-slate-400 group-hover:text-primary mt-0.5 transition-colors" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-slate-700 group-hover:text-primary transition-colors truncate">
                          <AutoTranslatedText text={event.name} />
                        </span>
                        <ChevronRight className="h-4 w-4 text-slate-300 group-hover:text-primary flex-shrink-0 transition-colors" />
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5">
                        {parseLocalDate(event.start_date).toLocaleDateString()} - {parseLocalDate(event.end_date).toLocaleDateString()}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Exhibitor Booths */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <SectionTitle icon={Building2} title={t('sitemap.exhibitors_section')} />
            {loading ? (
              <div className="animate-pulse space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-12 bg-slate-100 rounded-lg" />
                ))}
              </div>
            ) : booths.length === 0 ? (
              <p className="text-slate-500 text-sm py-4">{t('sitemap.no_exhibitors')}</p>
            ) : (
              <div className="space-y-1 max-h-80 overflow-y-auto">
                {booths.slice(0, 20).map((booth) => (
                  <Link 
                    key={booth.id}
                    to={`/booth/${booth.id}`}
                    className="group flex items-start gap-3 p-3 rounded-lg hover:bg-slate-50 transition-colors"
                  >
                    <Store className="h-5 w-5 text-slate-400 group-hover:text-primary mt-0.5 transition-colors" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-slate-700 group-hover:text-primary transition-colors truncate">
                          {booth.company_name}
                        </span>
                        <ChevronRight className="h-4 w-4 text-slate-300 group-hover:text-primary flex-shrink-0 transition-colors" />
                      </div>
                      {booth.slogan && (
                        <p className="text-xs text-slate-500 mt-0.5 truncate">
                          <AutoTranslatedText text={booth.slogan} />
                        </p>
                      )}
                    </div>
                  </Link>
                ))}
                {booths.length > 20 && (
                  <Link 
                    to="/exhibitors"
                    className="block text-center text-sm text-primary hover:underline py-2"
                  >
                    {t('sitemap.view_all_exhibitors')} ({booths.length})
                  </Link>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SitemapPage;
